<template>
  <svg :width="width" :height="height">
    <g v-if="data" :transform="graphTransform">
      <orthogonal-axes
         ref="axes"
         :xLabelRotate="xLabelRotate"
         :width="graphWidth"
         :height="graphHeight"
         :x="x"
         :y="y"
         :xTitle="xTitle"
         :yTitle="yTitle"
         :xFormatter="xFormatter"
         :yFormatter="yFormatter" />
      <g>
        <path
           v-for="(k, i) in keys"
           :class="lineClass(k)"
           fill="none"
           :stroke="color(k, i)"
           stroke-linejoin="round"
           stroke-linecap="round"
           stroke-width="1.5"
           :d="path(k)" />
        <g class="tooltip" >
          <template v-if="showTooltip" >
            <path :d="tooltipPath" fill="none" />
            <g v-for="(k, i) in keys" :transform="tooltipTransform(k)" >
              <circle fill="none" :stroke="color(k, i)" r="7" />
              <text v-bind="tooltipTextPosition(i)" :fill="color(k, i)" >
                {{ yFormatter(tooltipValue(k)) }}
              </text>
            </g>
          </template>
          <rect
             ref="mousearea"
             class="mousearea"
             :width="graphWidth > 0 ? graphWidth : 0"
             :height="graphHeight > 0 ? graphHeight : 0"
             @mouseover="showTooltip = true"
             @mouseout="showTooltip = false" />
        </g>
        <path
           class="path-selector"
           v-for="(k, i) in keys"
           fill="none"
           stroke="black"
           stroke-opacity="0"
           stroke-linejoin="round"
           stroke-linecap="round"
           stroke-width="5"
           :d="path(k)"
           @click="handleSelect(k)"
           @mouseover="handleLineMouseOver(k)"
           @mouseout="handleLineMouseOut" />
      </g>
      <template v-if="showLegend && keys.length > 1" >
        <draggable v-if="draggableLegend" ref="draggableLegend" >
          <graph-legend :keys="keyColors" :x="graphWidth - legendWidth" :width="legendWidth" />
        </draggable>
        <graph-legend v-else :keys="keyColors" :x="graphWidth - legendWidth" :width="legendWidth" />
      </template>
    </g>
  </svg>
</template>

<script>
import * as d3 from 'd3'
import * as d3Chromatic from 'd3-scale-chromatic'
import Line from './Line.vue'
import DraggableLegend from '../mixins/draggable-legend.js'
import selectable from '../mixins/selectable.js'

export default {
  extends: Line,
  mixins: [
    DraggableLegend,
    selectable
  ],
  props: {
    groupBy: {
      type: String
    },
    colorScale: {
      default: () => {
        return d3.scaleSequential(d3Chromatic.interpolateRainbow)
      }
    }
  },
  data () {
    return {
      hovered: null
    }
  },
  computed: {
    lineClass () {
      return k => [{
          hovered: this.hovered === k
      }].concat(this.selectableClass(k))
    },
    _color () {
      const scale = this.colorScale.domain([0, this.keys.length])
      return d3.scaleOrdinal(this.keys.map((k, i) => scale(i))).domain(this.keys)
    },
    keys () {
      if (!this.groupBy) {
        return [null]
      }
      const keys = {}
      this.data.forEach(d => {
        if (typeof keys[d[this.groupBy]] === 'undefined') {
          keys[d[this.groupBy]] = d[this.groupBy]
        }
      })
      return Object.keys(keys).map(k => keys[k])
    },
    keyColors () {
      return this.keys.map((k, i) => {
        return {
          label: k,
          color: this.color(i)
        }
      })
    },
    path () {
      return key => {
        if (key === null) {
          return this.line(this.data)
        }
        return this.line(this.data.filter(d => d[this.groupBy] === key))
      }
    },
    tooltipValue () {
      return y => {
        const d = this.data.find(
          d => d[this.xKey] === this.mouseX && d[this.groupBy] === y
        )
        return d ? d[this.yKey] : 0
      }
    }
  },
  methods: {
    selectData (d) {
      return {
        data: this.data.find(data => data[this.groupBy] === d),
        value: d
      }
    },
    selectKey (d) {
      return d.value
    },
    handleLineMouseOver (k) {
      this.showTooltip = true
      this.hovered = k
    },
    handleLineMouseOut () {
      this.showTooltip = false
      this.hovered = null
    },
    color (i) {
      return this._color(i)
    }
  }
}
</script>

<style scoped>
.mousearea {
    fill: none;
    stroke: none;
    pointer-events: all;
}
.tooltip path {
    stroke: #869fb8;
}
.hovered, .selected {
    stroke-width: 3;
}
.path-selector {
    cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// node_modules/d3vue/components/GroupableLine.vue